import { Link } from "react-router";
import { useReaction } from "../avatar";
import type { HeroMessage } from "./hero-messages";
import { useTypedMessage } from "./useTypedMessage";

type Props = { message: HeroMessage; order: number; ready: boolean };

/**
 * One thing Orla says, in a bubble whose tail points back at her. The visible text is
 * typed a character at a time and hidden from assistive technology; the finished line is
 * given to it once, when she has said it, so it is read out once and not on every letter.
 */
export function MessageBubble({ message, order, ready }: Props) {
  const { anchor, typed, complete } = useTypedMessage(message, order, ready);
  const reaction = useReaction({ gesture: "Wave" });
  return (
    <li
      ref={anchor}
      className="message-bubble"
      data-side={message.side}
      data-slot={message.slot}
      data-emphasis={message.emphasis}
      data-spoken={typed > 0}
    >
      <p className="message-bubble__body">
        <span aria-hidden="true">{message.text.slice(0, typed)}</span>
        {complete ? <span className="visually-hidden">{message.text}</span> : null}
        {typed > 0 && !complete ? (
          <span className="message-bubble__caret" aria-hidden="true" />
        ) : null}
      </p>
      {message.action && complete ? (
        <Link className="message-bubble__action" to={message.action.to} {...reaction}>
          {message.action.label}
          <span className="message-bubble__arrow" aria-hidden="true">
            &rarr;
          </span>
        </Link>
      ) : null}
    </li>
  );
}
